export const LOAN_DEFAULTS = {
  downPercent: 10,
  tenureYears: 9,
  annualRate: 3.8,
};

export const MIN_LOAN_AMOUNT = 20000;

export const TENURE_OPTIONS = [3, 5, 7, 9];

export function getMaxDownPayment(price, minLoan = MIN_LOAN_AMOUNT) {
  const vehiclePrice = Math.max(0, price);
  if (vehiclePrice <= 0 || vehiclePrice <= minLoan) return 0;
  return vehiclePrice - minLoan;
}

/** Exact % of vehicle price that yields min loan (may be fractional). */
export function getMaxDownPercent(price, minLoan = MIN_LOAN_AMOUNT) {
  const vehiclePrice = Math.max(0, price);
  if (vehiclePrice <= 0 || vehiclePrice <= minLoan) return 0;
  return (getMaxDownPayment(vehiclePrice, minLoan) / vehiclePrice) * 100;
}

/** Integer slider ceiling so max step caps loan at exactly minLoan. */
export function getSliderMaxDownPercent(price, minLoan = MIN_LOAN_AMOUNT) {
  const maxPct = getMaxDownPercent(price, minLoan);
  return maxPct <= 0 ? 0 : Math.ceil(maxPct);
}

export function getDownPaymentFromPercent(price, downPercent, minLoan = MIN_LOAN_AMOUNT) {
  const vehiclePrice = Math.max(0, price);
  const maxDown = getMaxDownPayment(vehiclePrice, minLoan);
  if (maxDown <= 0) return 0;
  const maxSliderPct = getSliderMaxDownPercent(vehiclePrice, minLoan);
  const pct = clamp(downPercent, 0, maxSliderPct);
  return Math.min(vehiclePrice * (pct / 100), maxDown);
}

export function parsePrice(priceStr) {
  if (priceStr == null || priceStr === '') return 0;
  const num = parseFloat(String(priceStr).replace(/[^\d.]/g, ''));
  return Number.isFinite(num) ? num : 0;
}

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function formatRM(amount) {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return `RM ${Math.round(amount).toLocaleString('en-MY')}`;
}

export function formatInstallmentLabel(amount) {
  if (amount == null || !Number.isFinite(amount)) return '';
  return `RM ${Math.round(amount)}/mth`;
}

/**
 * Reducing-balance monthly installment (hire purchase style).
 */
export function calculateLoan({
  price,
  downPercent = LOAN_DEFAULTS.downPercent,
  tenureYears = LOAN_DEFAULTS.tenureYears,
  annualRate = LOAN_DEFAULTS.annualRate,
}) {
  const vehiclePrice = Math.max(0, price);
  const down = clamp(downPercent, 0, getSliderMaxDownPercent(vehiclePrice));
  const years = clamp(tenureYears, 1, 9);
  const rate = clamp(annualRate, 0, 15);

  const downPayment = getDownPaymentFromPercent(vehiclePrice, down);
  const loanAmount = vehiclePrice - downPayment;
  const n = years * 12;

  if (loanAmount <= 0) {
    return {
      loanAmount: 0,
      monthlyPayment: 0,
      totalPayable: 0,
      totalInterest: 0,
      valid: true,
    };
  }

  if (rate === 0) {
    const monthlyPayment = loanAmount / n;
    return {
      loanAmount,
      monthlyPayment,
      totalPayable: monthlyPayment * n,
      totalInterest: 0,
      valid: true,
    };
  }

  const monthlyRate = rate / 100 / 12;
  const factor = Math.pow(1 + monthlyRate, n);
  const monthlyPayment =
    (loanAmount * monthlyRate * factor) / (factor - 1);

  const totalPayable = monthlyPayment * n;
  const totalInterest = totalPayable - loanAmount;

  return {
    loanAmount,
    monthlyPayment,
    totalPayable,
    totalInterest,
    valid: Number.isFinite(monthlyPayment) && monthlyPayment > 0,
  };
}

export function getEstimatedInstallment(price, overrides = {}) {
  const { monthlyPayment } = calculateLoan({
    price: parsePrice(price),
    ...LOAN_DEFAULTS,
    ...overrides,
  });
  return monthlyPayment;
}

export function getLowestVariantPrice(car) {
  if (!car?.variants?.length) return parsePrice(car?.price);
  return Math.min(...car.variants.map((v) => parsePrice(v.price)));
}

export function getEstimatedInstallmentForCar(car, overrides = {}) {
  return getEstimatedInstallment(getLowestVariantPrice(car), overrides);
}
